import {Sized, PositionedDimensional, Node, Edge} from './types';
import {NODE_RADIUS, CONFIG} from './constants';


const {PI, min, cos, sin, atan2, sqrt} = Math;

export const findCenter = ({x, y, h, w}: PositionedDimensional) => {
  return {
    x: x + w / 2,
    y: y + h / 2,
  };
};

export const makeNodeAndEdgePositioner =
  ({width, height}: Sized) =>
    <T extends Node, U extends Edge>({edges = {}, nodes = {}}: {edges: Record<string, U>, nodes: Record<string, T> }) => {
      if (Object.keys(nodes).length < 1) {
        return {
          nodes: [],
          edges: [],
        };
      }

      const nodePositioner = makeNodePositioner({width, height});
      const positionedNodes = nodePositioner(nodes);

      if (positionedNodes.length < 2) {
        return {
          nodes: positionedNodes,
          edges: [],
        };
      }

      const positionedEdges = Object.entries(edges).map(([id, edge]) => {
        const connectedNodes = positionedNodes.filter(
            ({id: nId}) => nId === edge[0] || nId === edge[1],
        );

        const [node1, node2] = connectedNodes;
        return {
          ...edge,
          id,
          start: {x: node1.x, y: node1.y},
          end: {x: node2.x, y: node2.y},
        };
      });
      return {
        nodes: positionedNodes,
        edges: positionedEdges,
      };
    };

export const makeNodePositioner =
  ({width, height}: Sized) =>
    <T extends Node>(nodes: Record<string, T> = {}) => {
      const nodeList = Object.entries(nodes);

      if (nodeList.length < 1) {
        return [];
      }

      const len = nodeList.length;
      return nodeList.map(([id, node], i) => {
        const [x, y] = toCartesian(
            min(height, width) / CONFIG.RADIUS_FACTOR,
            (i * 2 * PI) / len,
        );
        return {
          ...node,
          id,
          h: NODE_RADIUS * 2,
          w: NODE_RADIUS * 2,
          x,
          y,
        };
      });
    };

/**
 * Convert pola coordinates to catesian coordinates
 *
 * @param r - the coordinate magnitude
 * @param theta - coordiate rotation in radians
 */
export const toCartesian = (r: number, theta: number) => [r * cos(theta), r * sin(theta)];

export const toPolar = (x: number, y: number) => {
  const r = sqrt(x ** 2 + y ** 2);
  const theta = atan2(y, x);

  return {
    magnitude: r,
    angle: theta,
  };
};

export const positionElement = (element: HTMLElement, {x, y, h, w}: PositionedDimensional) => {
  element.style.top = `${y - h / 2}px`;
  element.style.left = `${x - w / 2}px`;
};

export const makeNewFrameOfReference =
  (offsetX: number, offsetY: number) =>
    (el: HTMLElement, {x, y, h, w}: PositionedDimensional) =>
      positionElement(el, {x: offsetX + x, y: offsetY + y, h, w});

